import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PenSquare, FileText, Globe, PenLine, Bell } from 'lucide-react';
import Card from '../../components/MasterAdmin/ui/Card';
import { getBlogsByAuthor } from '../../services/Writer/blogService';
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  type MasterAdminNotificationRow,
} from '../../services/MasterAdmin/masterAdminNotificationService';
import {
  formatNotificationBody,
  getNotificationLink,
} from '../../utils/masterAdminNotifications';

type WriterBlog = {
  blogId: string;
  status: 'draft' | 'published';
};

const WriterDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const userId = useMemo(() => localStorage.getItem('userId') || '', []);
  const [loading, setLoading] = useState(true);
  const [blogs, setBlogs] = useState<WriterBlog[]>([]);
  const [notifLoading, setNotifLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentNotifications, setRecentNotifications] = useState<MasterAdminNotificationRow[]>([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!userId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const myBlogs = await getBlogsByAuthor(userId);
        if (mounted) setBlogs(Array.isArray(myBlogs) ? myBlogs : []);
      } catch (error) {
        console.error('Failed to load writer blog stats:', error);
        if (mounted) setBlogs([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [userId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setNotifLoading(true);
        const [listRes, count] = await Promise.all([
          fetchNotifications({ page: 1, limit: 8 }),
          fetchUnreadCount(),
        ]);
        if (!mounted) return;
        setRecentNotifications(listRes.data.notifications || []);
        setUnreadCount(count || 0);
      } catch (error) {
        console.error('Failed to load writer notifications:', error);
        if (mounted) {
          setRecentNotifications([]);
          setUnreadCount(0);
        }
      } finally {
        if (mounted) setNotifLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const totalBlogs = blogs.length;
  const publishedBlogs = blogs.filter((b) => b.status === 'published').length;
  const draftBlogs = blogs.filter((b) => b.status === 'draft').length;

  const showCount = (value: number) => {
    if (loading) return 'Loading...';
    return value.toLocaleString();
  };

  const openNotification = async (n: MasterAdminNotificationRow) => {
    if (!n.isRead) {
      try {
        await markNotificationRead(n.id);
        setRecentNotifications((prev) =>
          prev.map((x) =>
            x.id === n.id ? { ...x, isRead: true, readAt: new Date().toISOString() } : x
          )
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        /* ignore */
      }
    }
    const href = getNotificationLink(n);
    if (href) navigate(href);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Writer Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Create and manage your blog content.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card
          title="Total Blogs"
          value={showCount(totalBlogs)}
          loading={loading}
          icon={<FileText className="w-6 h-6 text-primary-600 dark:text-primary-400" />}
        />
        <Card
          title="Published"
          value={showCount(publishedBlogs)}
          loading={loading}
          icon={<Globe className="w-6 h-6 text-green-600 dark:text-green-400" />}
        />
        <Card
          title="Drafts"
          value={showCount(draftBlogs)}
          loading={loading}
          icon={<PenLine className="w-6 h-6 text-amber-600 dark:text-amber-400" />}
        />
        <Card
          title="Unread notifications"
          value={notifLoading ? 'Loading...' : unreadCount.toLocaleString()}
          loading={notifLoading}
          icon={<Bell className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />}
          onClick={() => navigate('/writer/notifications')}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Writer notifications
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Doctor blog submissions awaiting your review
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/writer/notifications')}
              className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
            >
              View all
            </button>
          </div>

          {notifLoading ? (
            <p className="py-8 text-center text-sm text-gray-500">Loading notifications…</p>
          ) : recentNotifications.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No writer notifications yet.
            </p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {recentNotifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => openNotification(n)}
                  className={`w-full px-1 py-3 text-left transition hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                    !n.isRead ? 'bg-primary-50/40 dark:bg-primary-900/10' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                          {n.title}
                        </p>
                        {!n.isRead && (
                          <span className="h-2 w-2 shrink-0 rounded-full bg-primary-500" />
                        )}
                      </div>
                      <p className="mt-0.5 line-clamp-2 text-sm text-gray-600 dark:text-gray-300">
                        {formatNotificationBody(n)}
                      </p>
                    </div>
                    <span className="shrink-0 text-[11px] text-gray-400">
                      {new Date(n.createdAt).toLocaleString()}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={() => navigate('/writer/blogs')}
          className="text-left bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all h-fit"
        >
          <div className="w-12 h-12 rounded-xl bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center mb-4">
            <PenSquare className="w-6 h-6 text-primary-600 dark:text-primary-300" />
          </div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white">Health Blogs</div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {loading
              ? 'Loading blog count...'
              : `${totalBlogs} blog${totalBlogs === 1 ? '' : 's'} total`}
            {!loading && totalBlogs > 0
              ? ` · ${publishedBlogs} published · ${draftBlogs} draft`
              : ''}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            View, create, edit, and manage categories.
          </div>
        </button>
      </div>
    </div>
  );
};

export default WriterDashboardPage;
